import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Compass,
  GraduationCap,
  BookOpen,
  ChevronRight,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Button } from "../ui/button";

const PATHWAYS = [
  {
    id: "counselling",
    label: "Career & Stream",
    venture: "CounselNest",
    icon: Compass,
    division: "Career Intelligence",
    tagline: "Cognitive Aptitude & Guidance",
    badge: "Class 8 – 12",
    title: "Scientific Stream Selection & Psychometrics",
    desc: "Discover innate cognitive strengths, aptitudes, and personality matches before making irreversible stream decisions.",
    milestones: [
      "Standardized Psychometric & DMIT scientific assessments",
      "1-on-1 parent and student discovery counselling",
      "Structured 5-year personalized academic roadmap",
    ],
    link: "https://counselnest.in",
  },
  {
    id: "admissions",
    label: "University Admissions",
    venture: "AdmissionsNest",
    icon: GraduationCap,
    division: "Admissions Advisory",
    tagline: "Global University & Aid Strategy",
    badge: "India & Overseas",
    title: "End-to-End College & Scholarship Strategy",
    desc: "Targeted university shortlisting, SOP writing, entrance counselling (JEE/NEET/CUET), and visa documentation for 400+ colleges.",
    milestones: [
      "Global university discovery & strategic profile building",
      "Scholarship identification & application reviews",
      "Entrance cut-offs & direct branch matrices",
    ],
    link: "https://admissionsnest.in",
  },
  {
    id: "tutoring",
    label: "Targeted Tutoring",
    venture: "TutorsNest",
    icon: BookOpen,
    division: "Academic Excellence",
    tagline: "1-on-1 Subject Mentorship",
    badge: "Personalised Learning",
    title: "Outcome-Driven Subject Educators",
    desc: "Target conceptual gaps with verified top-percentile tutors for CBSE, ICSE, IB, Cambridge, and competitive entrance exams.",
    milestones: [
      "Curriculum & textbook-aligned master classes",
      "Doubt-clearing & conceptual problem solving",
      "Outcome tracking with weekly family reports",
    ],
    link: "https://tutorsnest.in",
  },
];

const TRUST_TAGS = [
  "Govt. of India Recognized Startup",
  "DPIIT Recognized",
  "Top 1% Verified Mentors",
  "Zero Fragmentation",
];

export default function HeroSection() {
  const [activePathway, setActivePathway] = useState(PATHWAYS[0]);

  return (
    <section className="relative overflow-hidden pt-4 pb-12 sm:pt-6 sm:pb-16 lg:pb-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          
          {/* Left Column: Hero Copy & Actions (Col 7) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            {/* Animated Announcement Pill */}
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200/90 bg-white/90 px-4 py-1.5 text-xs font-semibold text-slate-800 shadow-sm backdrop-blur-md">
              <span className="flex h-2 w-2 rounded-full bg-[#0B5D4B] motion-safe:animate-pulse" />
              <span className="bg-gradient-to-r from-[#0B5D4B] to-[#0D9488] bg-clip-text text-transparent font-bold">
                Next-Gen Education Ecosystem
              </span>
              <span className="text-slate-300">|</span>
              <span className="text-slate-600 font-normal">All Verticals Connected</span>
            </div>

            {/* Impact Headline */}
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl leading-[1.12]">
              One Ecosystem for{" "}
              <span className="bg-gradient-to-r from-[#0B5D4B] via-[#0F766E] to-[#14B8A6] bg-clip-text text-transparent">
                Every Student Journey.
              </span>
            </h1>

            {/* Subtext */}
            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600">
              An integrated ecosystem uniting career guidance, scientific assessments,
              higher-education admissions, and expert tutoring. One ecosystem. Multiple student needs.
              Human expertise powered by technology.
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-3.5 sm:gap-4">
              <Button
                onClick={() =>
                  document.getElementById("ventures")?.scrollIntoView({ behavior: "smooth" })
                }
                className="h-12 sm:h-13 rounded-full bg-gradient-to-r from-[#0B5D4B] to-[#064E3B] px-7 text-sm sm:text-base font-semibold text-white shadow-md shadow-[#0B5D4B]/20 hover:shadow-lg hover:shadow-[#0B5D4B]/30 hover:scale-[1.02] active:scale-95 transition-all duration-200"
                data-testid="hero-explore-btn"
              >
                Explore Ventures <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>

            {/* Trust Markers Bar */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-black/5 pt-5 text-xs font-medium text-gray-500">
              {TRUST_TAGS.map((tag) => (
                <div key={tag} className="flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-[#0B5D4B]" />
                  <span>{tag}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Premium Classic Business Card Visualizer (Col 5) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl border border-slate-200/80 bg-white/90 p-5 sm:p-6 shadow-[0_20px_50px_rgba(6,59,48,0.08)] backdrop-blur-2xl">
              
              {/* Header: Understated & Elegant */}
              <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="flex h-2 w-2 rounded-full bg-[#C89B3C]" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0B5D4B]">
                    Interactive Pathway
                  </span>
                </div>
              </div>

              {/* Pathway Switcher Tabs */}
              <div className="mt-4 grid grid-cols-3 gap-1 rounded-xl bg-slate-100/80 p-1 border border-slate-200/60">
                {PATHWAYS.map((p) => {
                  const Icon = p.icon;
                  const isActive = activePathway.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setActivePathway(p)}
                      className={`flex items-center justify-center gap-1.5 rounded-lg py-2 px-2 text-xs font-semibold transition-all duration-200 ${
                        isActive
                          ? "bg-white text-[#0B5D4B] shadow-sm font-bold border border-slate-200/70"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      <Icon size={13} className={isActive ? "text-[#C89B3C]" : "text-slate-400"} />
                      <span className="truncate">{p.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Classic Luxury Business Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePathway.id}
                  initial={{ opacity: 0, y: 8, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.99 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="relative mt-4 overflow-hidden rounded-2xl border border-[#C89B3C]/35 bg-gradient-to-br from-[#062D24] via-[#09382D] to-[#042019] p-6 text-white shadow-xl shadow-[#062D24]/35 group"
                >
                  {/* Subtle Soft Ambient Vignette */}
                  <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#C89B3C]/10 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-[#0B5D4B]/30 blur-3xl" />

                  {/* Card Top: Refined Branding */}
                  <div className="relative flex items-center justify-between pb-3.5 border-b border-white/10">
                    <div className="flex items-center gap-2.5">
                      <img
                        src="/crescet-favicon.png"
                        alt="Crescet Logo"
                        className="h-7 w-7 object-contain brightness-110 drop-shadow-sm"
                      />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C89B3C]">
                          CRESCET EDUSTREAMS
                        </span>
                        <span className="text-[8.5px] font-medium text-emerald-100/60 uppercase tracking-widest">
                          {activePathway.division}
                        </span>
                      </div>
                    </div>

                    <span className="rounded-full border border-[#C89B3C]/30 bg-[#C89B3C]/10 px-2.5 py-0.5 text-[9.5px] font-semibold text-[#F3E5AB] tracking-wide">
                      {activePathway.badge}
                    </span>
                  </div>

                  {/* Card Main: Venture Name & Hallmarks */}
                  <div className="relative mt-4">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C89B3C]/90">
                      {activePathway.tagline}
                    </span>

                    <h3 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-white mt-1">
                      {activePathway.venture}
                    </h3>

                    <p className="mt-2 text-xs leading-relaxed text-emerald-100/80">
                      {activePathway.desc}
                    </p>

                    {/* Subtle Gold Hairline Divider */}
                    <div className="my-3.5 h-px w-full bg-gradient-to-r from-[#C89B3C]/40 via-white/10 to-transparent" />

                    {/* Executive Hallmarks / Milestones */}
                    <div className="space-y-2">
                      {activePathway.milestones.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 text-xs text-emerald-50/95 font-medium">
                          <span className="text-[#C89B3C] text-[9px] mt-1 leading-none">◆</span>
                          <span className="leading-snug">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Card Bottom: Classic Executive Footer */}
                  <div className="relative mt-5 pt-3.5 border-t border-white/10 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-wider text-emerald-200/60 font-medium">
                      <span>Verified Pathway</span>
                      <span>•</span>
                      <span className="text-[#C89B3C]/80">ISO 9001:2015</span>
                    </div>

                    <a
                      href={activePathway.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-xl bg-[#C89B3C] hover:bg-[#D9AC48] text-[#042019] px-3.5 py-1.5 text-xs font-bold transition-all duration-200 shadow-sm hover:scale-[1.02] active:scale-95"
                    >
                      <span>Explore {activePathway.venture}</span>
                      <ChevronRight size={13} className="stroke-[2.5]" />
                    </a>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
